import {
  Armchair,
  Bath,
  BedDouble,
  BrushCleaning,
  CookingPot,
  DoorClosed,
  TableProperties,
  Toilet,
  Trash2,
  type LucideIcon,
} from 'lucide-react'

/**
 * Controlled apartment feature values for the Apartments collection.
 * Add new features here when inventory introduces a new category.
 */
export const apartmentFeatureIds = [
  'privateToilet',
  'privateBathroom',
  'privateKitchen',
  'desk',
  'deskChair',
  'wardrobe',
  'bed',
  'trashCan',
  'dustpanAndBrush',
] as const

export type ApartmentFeatureId = (typeof apartmentFeatureIds)[number]

export const apartmentFeatureSelectOptions = [
  { label: 'Private toilet', value: 'privateToilet' },
  { label: 'Private bathroom', value: 'privateBathroom' },
  { label: 'Private kitchen', value: 'privateKitchen' },
  { label: 'Desk', value: 'desk' },
  { label: 'Desk chair', value: 'deskChair' },
  { label: 'Wardrobe', value: 'wardrobe' },
  { label: 'Bed', value: 'bed' },
  { label: 'Trash can', value: 'trashCan' },
  { label: 'Dustpan and brush', value: 'dustpanAndBrush' },
] as const

export const apartmentFeatureIconComponents: Record<ApartmentFeatureId, LucideIcon> = {
  privateToilet: Toilet,
  privateBathroom: Bath,
  privateKitchen: CookingPot,
  desk: TableProperties,
  deskChair: Armchair,
  wardrobe: DoorClosed,
  bed: BedDouble,
  trashCan: Trash2,
  dustpanAndBrush: BrushCleaning,
}

const APARTMENT_FEATURE_LABELS = Object.fromEntries(
  apartmentFeatureSelectOptions.map((option) => [option.value, option.label]),
) as Record<ApartmentFeatureId, string>

export function getApartmentFeatureLabel(featureId: ApartmentFeatureId): string {
  return APARTMENT_FEATURE_LABELS[featureId] ?? featureId
}

export function isApartmentFeatureId(value: string): value is ApartmentFeatureId {
  return (apartmentFeatureIds as readonly string[]).includes(value)
}

export function resolveApartmentFeatures(
  features: readonly string[] | null | undefined,
): ApartmentFeatureId[] {
  if (!features?.length) return []

  const selected = new Set(features.filter(isApartmentFeatureId))
  return apartmentFeatureIds.filter((featureId) => selected.has(featureId))
}
